// The papers kept per aircraft (flight.aircraft_documents). One list, so the
// database CHECK, the admin form, the aircraft page, the expiry report and
// the reminder email agree. Keep it in step with V1030__aircraft_documents.sql.

// The papers that run out. A tuple, so the expiry email's zod enum can be
// built from it.
export const EXPIRING_DOCUMENT_TYPES = [
  'arc',
  'insurance',
  'kausikortti',
  'radioLicence',
  'cofa',
  'noise',
  'registration',
  'modeS',
  'wbReport',
] as const
export type ExpiringDocumentType = (typeof EXPIRING_DOCUMENT_TYPES)[number]

// The Pilot's Operating Handbook and the checklist have their own buttons,
// not rows in the list: a pilot on the ramp must be one tap from them. They
// have a scan and no expiry.
export const POH_DOCUMENT = 'poh'
export const CHECKLIST_DOCUMENT = 'checklist'
export const SCAN_ONLY_DOCUMENT_TYPES = [POH_DOCUMENT, CHECKLIST_DOCUMENT] as const
export const AIRCRAFT_DOCUMENT_TYPES = [...EXPIRING_DOCUMENT_TYPES, ...SCAN_ONLY_DOCUMENT_TYPES] as const
export type AircraftDocumentType = (typeof AIRCRAFT_DOCUMENT_TYPES)[number]

export const isScanOnly = (type: string): boolean => (SCAN_ONLY_DOCUMENT_TYPES as readonly string[]).includes(type)

// Insurance papers carry policy and payment details, so only whoever edits
// the fleet may open that scan; every other scan is for every member.
export const RESTRICTED_SCAN_TYPES: readonly AircraftDocumentType[] = ['insurance']

// GET /api/aircraft/:registration/documents
export interface AircraftDocument {
  documentType: AircraftDocumentType
  expiryDate: string | null
  hasScan: boolean
  filename: string | null
  uploadedAt: string | null
}

// Whole days from `today` to `expiryDate` (both YYYY-MM-DD, `today` in the
// club's timezone): negative once expired, 0 on the day itself.
export function daysUntilExpiry(expiryDate: string, today: string): number {
  return Math.round((Date.parse(`${expiryDate}T00:00:00Z`) - Date.parse(`${today}T00:00:00Z`)) / 86_400_000)
}

// Soonest expiry first; papers with no date after the dated ones.
export function sortDocuments<T extends { expiryDate: string | null; documentType: string }>(documents: readonly T[]): T[] {
  return [...documents].sort((a, b) => {
    if (a.expiryDate && b.expiryDate) return a.expiryDate.localeCompare(b.expiryDate) || a.documentType.localeCompare(b.documentType)
    if (a.expiryDate) return -1
    if (b.expiryDate) return 1
    return a.documentType.localeCompare(b.documentType)
  })
}
